import React, { useEffect, useRef, useState } from "react";
import { Flame, CheckCircle2, Loader2 } from "lucide-react";
import api, { formatError } from "@/lib/api";
import { toast } from "@/components/Toaster";

/**
 * Admin-only "Warm cache" button.
 *
 * Clicking starts a server-side background sweep that builds the
 * flat-PDF cache for every uncached DOCX / PDF in the room. The first
 * download of each file becomes instant for *every* user once warming
 * is complete (the cache is shared, not per-viewer).
 *
 * UI states:
 *   • idle     — solid neutral button, label "Warm cache"
 *   • running  — animated flame + "Warming 47 / 156" with thin progress
 *                bar, polls /cache/warm/status every 1.5 s
 *   • done     — checkmark + transient success toast, fades back to idle
 *
 * The component is intentionally self-contained — it manages its own
 * polling lifecycle, aborts on unmount, and never blocks the rest of
 * the toolbar from rendering while a sweep is in flight.
 */
export default function WarmCacheButton({ roomId, className = "" }) {
  const [job, setJob] = useState(null);          // null | { state, total, done, failed }
  const [busy, setBusy] = useState(false);
  const timerRef = useRef(null);

  // On mount, ask the backend for any job currently running for this
  // room — covers the case where an admin warmed the room, navigated
  // away, and came back mid-sweep on a different tab/device.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/rooms/${roomId}/cache/warm/status`);
        if (!cancelled) {
          setJob(data?.job || null);
          if (data?.job?.state === "running") startPolling();
        }
      } catch (_) {/* idle is the sensible default */}
    })();
    return () => {
      cancelled = true;
      stopPolling();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomId]);

  const stopPolling = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const startPolling = () => {
    stopPolling();
    timerRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/rooms/${roomId}/cache/warm/status`);
        const j = data?.job;
        setJob(j || null);
        if (!j || (j.state !== "running")) {
          stopPolling();
          if (j?.state === "done") {
            toast.success(
              `Cache warmed — ${j.done} file${j.done === 1 ? "" : "s"} ready`
              + (j.failed ? ` · ${j.failed} failed` : "")
            );
          } else if (j?.state === "error") {
            toast.error("Cache warm failed — see server logs.");
          }
        }
      } catch (_) {/* keep trying — the next tick may succeed */}
    }, 1500);
  };

  const start = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/rooms/${roomId}/cache/warm`);
      setJob(data?.job || null);
      if (data?.job?.state === "running") {
        startPolling();
      } else if (data?.job?.state === "done") {
        toast.success("Nothing to warm — everything is already cached.");
      }
    } catch (e) {
      toast.error(`Could not start warm: ${formatError(e)}`);
    } finally {
      setBusy(false);
    }
  };

  const isRunning = job?.state === "running";
  const isDone = job?.state === "done" && job?.total > 0;

  // Render — three visual variants share the same outer shell so the
  // toolbar height stays constant as state changes.
  const baseCls =
    "inline-flex items-center gap-2 px-3 py-2 text-sm rounded-md border transition";
  const stateCls = isRunning
    ? "border-amber-500/40 bg-amber-500/5 text-amber-700 dark:text-amber-300"
    : isDone
    ? "border-emerald-500/40 bg-emerald-500/5 text-emerald-700 dark:text-emerald-300"
    : "border-border hover:bg-secondary";

  return (
    <div className={`flex flex-col items-end gap-1 ${className}`}>
      <button
        data-testid="doc-warm-cache-btn"
        onClick={start}
        disabled={busy || isRunning}
        className={`${baseCls} ${stateCls} disabled:opacity-90 disabled:cursor-not-allowed`}
        title={
          isRunning
            ? "Pre-building the flat-PDF cache for every file in the room — runs in the background."
            : "Pre-build the flat-PDF cache so every user's first download is instant."
        }
      >
        {isRunning ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : isDone ? (
          <CheckCircle2 className="w-4 h-4" />
        ) : (
          <Flame className="w-4 h-4 text-orange-500" />
        )}
        {isRunning
          ? `Warming ${job.done} / ${job.total}`
          : isDone
          ? `Cache ready (${job.done})`
          : "Warm cache"}
      </button>
      {isRunning && (
        <div
          data-testid="doc-warm-cache-progress"
          className="w-40 h-1 rounded-full bg-secondary overflow-hidden"
          aria-label="warm progress"
        >
          <div
            className="h-full bg-amber-500 transition-all duration-700"
            style={{
              width: `${job.total ? (job.done / job.total) * 100 : 0}%`,
            }}
          />
        </div>
      )}
    </div>
  );
}
